// What the UI knows about a site's carried devices (ZENITH.md §11.2,
// ADR-0019). Shared by the Carry-over page, the Devices table and the
// Networks plan, so the three cannot disagree about whether a site is frozen.
//
// **There is no endpoint that answers "is this device carried".** `Device` is
// read live from the site's cell and Zenith stores no device record (§11.2),
// while the configuration source lives in Zenith's own table. The one place
// the two meet in the public API is `/sites/{id}/cutover-readiness`, which
// reports every claimed access point with a reason, so this module derives
// the marker by joining on the serial rather than asking for a field the
// document does not declare. Adding one would be an API change and this
// item's Out says the API gets it first, in its own work item (#151).
//
// **The join costs a second read of the cell's inventory.** Readiness reads
// the venue's devices itself, so a page that already lists them asks twice.
// That is the price of deriving rather than carrying a field, and it is the
// right price: `Device` is the cell's record, read live and never stored
// (§11.2), while the configuration source is Zenith's own — putting one on
// the other would mix the two stores in the schema that exists to keep them
// apart. The readiness resource is the deliberate place they meet.
import type { ReactNode } from "react";
import type { components } from "./api/schema";
import { api } from "./api/client";
import { SKIP, useQuery } from "./api/hooks";
import { ScopeLink } from "./shell/scope";
import { LearnMore, Pill } from "./ui";
import { listed, unrecognized } from "./api/openEnum";

export type Readiness = components["schemas"]["CutoverReadiness"];
export type DeviceReadiness = components["schemas"]["DeviceReadiness"];
export type ExitReading = components["schemas"]["ExitReading"];
type ExitOutcome = components["schemas"]["ExitReadingOutcome"];
// Keyed on the named lists: since ADR-0511 each field is its list or any other string.
type Reason = components["schemas"]["DeviceReadinessReason"];

/** What each per-device reason means and what to go and do about it. The
 * reason is the product of the readiness endpoint, not the flag: "not ready"
 * without one asks an operator to read a database (#150). */
export const DEVICE_REASON: Record<Reason, { label: string; tone: "good" | "warn" | "serious"; next: string }> = {
  // Restates ADR-0144 decisions 1 and 7 — an access point whose firmware nobody could state is
  // imported, not refused, and reads `unqualified_pending_firmware` below; so a device here was
  // never imported, or was refused for something else. This said "Import the export that covers
  // it" until #852, which was false of that device: its export did cover it and the import
  // refused the whole upload. If such a device is ever refused again, this sentence goes false.
  not_carried: {
    label: "not imported",
    tone: "serious",
    next: "This access point has no carried configuration. Either it was never in an import, or the import refused it, for example because no qualified schema matches the firmware it states. To bring it in, import an export that includes it, and declare its firmware if the export does not state one.",
  },
  // Restates ADR-0019 decision 6 and ADR-0213 decision 4a — `not applied` is one fact now: no
  // carried apply to the device has completed. The second cause #1186 folded in here, an apply whose answer carried no
  // generation, is `sent_not_confirmed` below. If the two are ever folded back together, this
  // sentence is the one that goes false.
  not_applied: {
    label: "not applied",
    tone: "warn",
    next: "No attempt to apply this configuration to the controller has completed. Plan it, read what will be sent, then apply.",
  },
  // Restates ADR-0213 decision 4a and #1186 — a carried apply to this device ran and the
  // controller's answer carried no generation, so nothing is the controller's own word that it took
  // the document. It is not a claim the bytes failed to arrive, nor that the device is not running
  // them, and the act is the same as `not applied`'s. It is told apart because on 2026-09-30 an
  // operator re-applied four healthy access points when the two read alike.
  sent_not_confirmed: {
    label: "sent, not confirmed",
    tone: "warn",
    next: "A carried configuration was sent to this access point, but the controller's answer carried no generation, so nothing here confirms that the controller took the configuration. That does not mean the configuration failed to arrive, or that the access point is not running it. It may be. This is shown apart from not applied because it is not the same fact: something was sent. What to do is the same either way: plan it, read what will be sent, then apply.",
  },
  stale: {
    label: "stale",
    tone: "warn",
    next: "A configuration was applied and the site has been imported again since, so what is on the controller is not what would be sent today. Plan and apply again.",
  },
  // Restates ADR-0051 decision 5, #612, #1287 — the readiness refusal names the property and the
  // release; each refused property is listed with the rule the release refused it by and never
  // the value the document carried, because a carried document's refused property may be
  // material ADR-0062 decision 6 seals whole. The "listed beside the device" half is what
  // #794 made true: DeviceReadiness.refusals had been in the contract since ADR-0051 and was
  // rendered nowhere. #1287 is the one exception, and the copy says so: an SSID's security
  // mode (`encryption.proto`) is named, because it is a vocabulary word and not a secret.
  refused_by_cell_schema: {
    label: "the controller refuses this configuration",
    tone: "serious",
    next: "The release this site's controller runs refuses this stored configuration, even after it is narrowed for that release. Applying again will not help. A controller release that accepts those properties will. Each refused property is listed beside the device with the rule that refused it, never your value. Open the configuration for that. The one value shown is an SSID's security mode, where that is what was refused, because it is a word and not a secret.",
  },
  // Restates ADR-0101 decision 2, ADR-0019 decision 7, #1362 — the device's own pin refuses the
  // stored document, so nothing the unattended seed or an apply does will deliver it, and what moves
  // it is a pin that admits the document or a document the pin admits — not a controller release,
  // which is the sibling `refused_by_cell_schema`'s remedy and the wrong thing to send an operator
  // looking for here. The seed asks again when either changes; nothing is retried meanwhile.
  // TODO(copy): kept but for its third sentence (#1678). "not unattended, and not if you apply
  // it" and "A controller release will not change that" each stop a wrong act, so the length
  // stays. "A corrected firmware for the device" reads "the firmware stated for the device is
  // corrected": the contract's own words for what makes the seed ask again are "a re-import, or
  // a changed stated firmware".
  undeliverable_at_pin: {
    label: "its firmware's schema refuses this configuration",
    tone: "serious",
    next: "The schema for the firmware this access point states refuses this stored configuration, and every send of it is checked against that schema, so it will not be sent — not unattended, and not if you apply it. A controller release will not change that. What does is a schema for this firmware that accepts what the configuration asks for, or a configuration that asks for something it accepts. After you import again, or after the firmware stated for the device is corrected, the configuration is checked again. Each refused property is listed beside the device with the rule that refused it, never your value. The one value shown is an SSID's security mode, where that is what was refused, because it is a word and not a secret.",
  },
  // Restates ADR-0144 decisions 3, 5 and 6 — the access point was imported with no firmware stated,
  // so its record holds no schema pin and nothing is sent to it: the seed skips it and a plan or
  // apply is refused. It is the site's readiness fact for a cutover, because a controller that
  // holds no document for an access point when it arrives gives it one of its own (`docs/evidence/`,
  // #146). What lifts it today is a declared firmware at a re-import. ADR-0086's comparison is built
  // (#1708) and does not lift it: restating the firmware records a statement and leaves the record
  // with no pin, so the device's own report is still not offered. If lifting through the comparison
  // lands, or a firmware is ever defaulted, the last sentence is the one that goes false.
  unqualified_pending_firmware: {
    label: "firmware not known",
    tone: "serious",
    next: "No firmware was stated for this access point when it was imported, so there is no schema to check its configuration against, and the configuration will not be sent: not unattended, and not if you apply it. The configuration is stored and the device is claimed to this site. While it reads this, the site is not ready for cutover: when the device reaches the controller, the controller will hold no configuration for it and will give it a configuration of its own. Declare the firmware this device runs and import the export again.",
  },
  // Restates ADR-0227 decision 2a as amended by ADR-0352, ADR-0254 decision 4.2a — where the cell
  // holds no record of a carried access point, a seed run writes its import-approved document as
  // that record, in place of the skip #425 ruled, and it does so on all three of the seed's
  // triggers: the run that follows an import, Seed the site, and the unattended pass. Until
  // ADR-0352 this said the unattended seed's next pass alone, which was never what was built. If a
  // trigger is ever narrowed out of the record verb, the sentence beginning "A seed run writes" is
  // the one that goes false. Until ADR-0227's build that sentence said the
  // cell would create the record on first connect and the next pass would seed it; that is what let
  // the cell mint `OpenWifi` first, and it is no longer what happens. The last two sentences are
  // ADR-0254's one fail-open, said as one: a device that has never connected is given its record
  // although nobody can tell whether its document moves its board-data group.
  // `docs/ZENITH.md` §11.2 quotes this label and the next one as they are written here. ADR-0208
  // records the first in the words of its day, with "cell" (#1678; `web/COPY.md`, *Where "cell"
  // stays*).
  // TODO(copy): kept whole (#1678). It is about 190 words in a table cell and a tooltip, and
  // each sentence is one the marker above names; `next` is a string two tooltips also read, so
  // none of it can move behind Learn more from here.
  not_on_cell: {
    label: "not on the controller yet",
    tone: "warn",
    next: "This site's controller has no record of this device, so no configuration can be sent to it. It is not the same as offline: a device the controller knows and that is disconnected has a record the controller can hold a configuration in. A seed run writes this device's imported configuration as its record on the controller (the run that follows an import, Seed the site, or the unattended seed's next pass, whichever reaches it first), so that when the device first connects the controller has that configuration to deliver and nothing to invent. Nothing has been spent and nothing needs resetting. If it stays here after a seed run, the controller could not be written to — the device's push history says why. It is given its record even though nobody can tell yet whether its configuration would reboot it: it has never connected, so the country it holds cannot be read. That is deliberate. Refusing it would leave it to the controller's own default configuration, which is what caused most of the reboots measured, and it may still reboot once when it first connects.",
  },
  // Restates ADR-0227 decisions 2a and 5a, ADR-0352, ADR-0270 decisions 1b and 2a, ADR-0379 — the cell holds the device's
  // document as its own record, written by a seed run on any of the seed's three triggers (the
  // run that follows an import, Seed the site, the unattended pass: ADR-0352; `SeedCarried` is
  // the one writer of a `carried_record`), no device has reported, and the word is fed from Zenith's push
  // rows and not from the gateway,
  // whose `online_state` for a record with no session reads `offline`. The device reads ready
  // only on the cell's report that it runs the generation the record was stamped with (ADR-0094's
  // predicate); a record the cell replaced first fails back to `not applied`. The seed then sends
  // again only until SEED_FAILED_WINDOW has passed since the document's first seed send, the
  // record write included, and after that the device reads `seed_not_transmitting`. "A day" is
  // that window, 24h in both environments by ADR-0270's ruling; if it moves, the last two
  // sentences are the ones that go false. ADR-0379 (ruled on #1696, 2026-10-13: *"ADR-0270's
  // window"*) rules that the window governs a replaced record and decision 5a's fallback does not
  // outlast it, so those two sentences are as ruled. It said "written there by the unattended
  // seed" until #1678 (ruled on that item on 2026-10-12, note `4001321718`).
  // This sentence is the carry-over seed's record's, and only that one's. Since ADR-0276 decision
  // 4a readiness also gives this reason to an access point of a generated site whose record an
  // operator's apply wrote (`generatedHeld` in `internal/configure/readiness.go`), and of that
  // device this sentence is false: its document is not imported and no seed run wrote it.
  // `DeviceReadiness.held_record` says which writer it was (#1703), and `reasonOf` below gives
  // that row `HELD_GENERATED` instead (#1715), so nothing that draws a readiness row's sentence
  // should read this entry for it directly.
  held_on_cell: {
    label: "held on the controller",
    tone: "warn",
    next: "This site's controller holds this device's imported configuration as the device's own record, written there by a seed run before the device arrived — and the device has not yet connected and reported running it. Nothing to do: when it first connects, the controller delivers that configuration, and once the controller reports the device running it this reads applied. The device shows as offline because the controller now has a record for it with no session; that is a fact about the record, not the device. If the controller's copy is replaced before the device arrives, this falls back to not applied. The seed sends the configuration again only within a day of its first send of it; after that this reads not seeded: no send reached it, and the route is to plan and apply this device yourself.",
  },
  // Restates ADR-0226 decision 1b, #438, ADR-0019 decision 10 — readiness gives this reason to
  // the access points the site's last reconciliation covered and to no others (`reconciledSet`
  // in `internal/configure/readiness.go`; nil means it covered them all), so the sentence is
  // about this access point. One left out stays carried on its own document. Nothing is sent to
  // a covered one unattended (#438), and its imported document is kept for the life of its
  // record. It said "This site has applied a venue configuration, so its access points are
  // meant to be running the site's generated document" until #1678, which ADR-0226 made wider
  // than the build (ruled on that item on 2026-10-12, note `4001321718`). If the reason is ever
  // reported for an access point no reconciliation covered, the first clause goes false. It said
  // "Nothing to do here: this is the destination, not a step on the way to it" until #2249, which
  // was false of every device it was shown for: readiness gives this reason to a covered access
  // point only while it is still `carried`, so the exit has yet to move it, and the exit's own
  // word for why (`DeviceReadiness.exit`) is in the notice every page that draws this tooltip shows.
  reconciled: {
    label: "reconciled",
    tone: "good",
    next: "A reconciliation at this site covered this access point, so it is meant to be running the site's generated configuration and not the vendor's configuration, and the carried configuration is deliberately not sent. The device's imported configuration is still kept, so nothing about where this venue came from is lost. It stays carried until the controller and the access point confirm it runs what the reconciliation sent, and the site's notice at the top of the page says why it has not yet.",
  },
  // Restates #1193's contract sentence for `refused_since_applied`, as narrowed by ADR-0277
  // decisions 1c and 2a: upstream, a device's `Rejects` means it applied the document and some
  // of what it asks for was not taken, so it is not running the unmodified document, and the
  // three devices sent the same document again on 2026-10-02 answered the same way. A
  // `Rejects` listing only the firmware's radio-mode fallback is not this reason: that device
  // reads ready with the fallback shown. If any of those stops being true, this copy is the
  // sentence that goes false.
  refused_since_applied: {
    label: "refused part of its configuration",
    tone: "serious",
    next: "This configuration was applied here once, and the most recent time it was sent the refusal shown below came back — usually the access point's own, listing what it rejected. An access point that answers this way has applied the configuration without some of what it asks for, which is not the unmodified configuration a cutover needs. Where the same configuration was sent again, it drew the same answer; what clears it is the access point taking the configuration without a refusal. An access point whose only answer is that a radio fell back to another mode is not shown here: it reads applied, with the fallback stated.",
  },
  // Restates ADR-0254 decisions 4.2a and 5 — the seed's sixth gate: a carried access point whose own
  // document would move it into another board-data group, or might, is not seeded; the firmware
  // reboots about 30 s after it answers, and no duration off the air is stated. The route is the
  // per-device apply with that line accepted (ADR-0044 decision 6).
  would_reboot: {
    label: "not seeded: this would reboot it",
    tone: "serious",
    next: "The unattended seed did not send this access point its imported configuration, because the configuration's country would move it into another board-data group from the one it holds, and the firmware reboots about 30 s after it answers. Where nobody can tell whether it would, it is not sent either. The line below says which, and names the move. Applying it yourself is the route: plan it, read the line, accept it, and apply.",
  },
  // Restates ADR-0270 decisions 1b, 2a, 3a and 4a — every seed send of this document ended
  // `failed` (nothing reached the device), the first is older than SEED_FAILED_WINDOW, and the
  // seed has stopped trying; until then it tried on every pass, with no wait between them. It is
  // not a refusal and not offline, a re-import starts a new window, and the route is the
  // per-device apply. If the window comes to reset on a gap (`1b′`, considered and not taken),
  // the "since its first try" sentence is the one that goes false.
  seed_not_transmitting: {
    label: "not seeded: no send reached it",
    tone: "serious",
    next: "The unattended seed has stopped sending this access point its imported configuration: no send since the first try shown below reached it (the access point was busy, or the controller withdrew or could not take the send), and the window for trying has passed. It is not a refusal and not offline — an offline device is never sent to. The newest answer is shown below. Apply it yourself: plan it, read what will be sent, and apply. Importing again starts a new window.",
  },
  unrecognised_source: {
    label: "source not understood",
    tone: "serious",
    next: "This device's configuration source says something this version does not understand. Nothing here will move it — an import cannot run, because a record already exists for this serial, and a generated configuration is refused. Someone has to find out what wrote it. Every write refuses rather than guessing, which is why the device is still running whatever it was running.",
  },
};

/** What a `held_on_cell` row says when the record the controller holds is an
 * operator's generated apply's and not the carry-over seed's (#1715). The
 * device is not carried, so it gets its own sentence and no `carried` marker.
 *
 * Restates ADR-0276 decision 4a, with decisions 1b, 2a, 5c and 7c — the
 * operator's apply wrote the record, holding the document the plan showed for
 * that access point; the push row is `stored` until the cell's own report, and
 * readiness says `held_on_cell` until then; nothing unattended writes a record,
 * and a unit whose record exists takes the apply's ordinary path. Nothing here
 * says the device runs the record's document once it connects (ruled on #1678,
 * 2026-10-12, as `DeviceRecordLines.tsx` records it): what it reports says so.
 * The offline sentence is the carried entry's, true of any record with no
 * session. If a standing pass ever writes a generated record, the last
 * sentence goes false. The wording is proposed on #1715's merge request and
 * not yet ruled. */
export const HELD_GENERATED: (typeof DEVICE_REASON)[Reason] = {
  label: "held on the controller",
  tone: "warn",
  next: "This site's controller holds the configuration generated for this access point as the device's own record. A plan applied from here wrote it there before the device arrived, and the device has not yet reported running it. Nothing to do until it connects: what it reports then says whether it runs that configuration. The device shows as offline because the controller now has a record for it with no session; that is a fact about the record, not the device. If the controller's copy is replaced before the device arrives, nothing writes it again unattended, and the device takes the plan's ordinary path.",
};

/** Whether readiness's row is `held_on_cell` from an operator's generated
 * apply's record, which is not carried (ADR-0276 decision 4a, #1703). */
export function heldGenerated(d: DeviceReadiness): boolean {
  return d.reason === "held_on_cell" && d.held_record === "generated_record";
}

/** What readiness's row says of its device: the reason's entry, except a
 * generated record's `held_on_cell`, which is `HELD_GENERATED`'s (#1715). */
export function reasonOf(d: DeviceReadiness): (typeof DEVICE_REASON)[Reason] | undefined {
  if (heldGenerated(d)) return HELD_GENERATED;
  if (!d.reason) return undefined;
  return listed(DEVICE_REASON, d.reason) ? DEVICE_REASON[d.reason] : unrecognisedReason(d.reason);
}

/** A reason this build does not know (ADR-0511 5a): shown as received, warned, and never read as
 * ready or as one of the listed reasons. */
function unrecognisedReason(reason: string): (typeof DEVICE_REASON)[Reason] {
  return {
    label: unrecognized(reason),
    tone: "warn",
    next: "This page does not recognize the reason the server gave for this device, so it cannot say what to do about it. Reload the page: a newer version may know it.",
  };
}

/** Why the site as a whole is not ready, where that is a property of the site
 * rather than of its devices. */
export const SITE_REASON: Record<components["schemas"]["CutoverReadinessReason"], string> = {
  // ZENITH.md §9.3.
  placement: "Configuration cannot currently be written for this site: its placement is not confirmed, or it is moving between controllers. Nothing can be seeded until that settles, so the site cannot become ready before then.",
  no_access_points: "No access point is claimed into this site. A site with none is reported as not ready, because reporting it ready would invite a redirect of nothing.",
};

export interface Carried {
  /** Serials of access points that hold a carried document. */
  serials: Set<string>;
  /** Serials whose configuration source this build does not understand. They
   * are counted apart from `serials` because they are a different thing to
   * say: a carried device is running a document Zenith holds, and one of
   * these is running something nobody here can account for. */
  unreadable: Set<string>;
  /** Whether a generated configuration is refused for this site.
   *
   * **This is now both sufficient and necessary**, which it was not before
   * #156. The server refuses on any source value that is not `generated`,
   * and readiness reports every one of them — `carried` as one of the three
   * seeding reasons, anything else as `unrecognised_source`. So the two sides
   * agree about every row, and a site this says nothing about is a site the
   * server will not refuse.
   *
   * The server's `carried` Problem is still the authority for *which* devices
   * and *which* imports, because only it has the row. This decides whether to
   * warn. */
  frozen: boolean;
  /** For each carried access point the site's last apply covered, the exit's own
   * word for why it has not left `carried` (#2249, `DeviceReadiness.exit`). */
  exits: Map<string, ExitReading>;
}

const EMPTY: Carried = { serials: new Set(), unreadable: new Set(), frozen: false, exits: new Map() };

/** Reads a site's readiness and derives what the other surfaces need.
 * A failure is not an assertion that nothing is carried: it leaves the
 * previous answer in place and reports nothing, so a page never tells an
 * operator a frozen site is free because a read failed. */
export function useCarried(siteId: string | undefined) {
  const q = useQuery(() => siteId
    ? api.GET("/sites/{id}/cutover-readiness", { params: { path: { id: siteId } } })
    : SKIP, [siteId]);
  return { ...q, carried: q.data ? derive(q.data) : EMPTY };
}

export function derive(r: Readiness): Carried {
  // Ready, waiting to be applied, or applied and since superseded: all three
  // mean the device holds a carried document.
  //
  // **Except a record an operator's generated apply wrote** (#1703). Readiness
  // gives `held_on_cell` to both writers' records (ADR-0227 decision 5a, ADR-0276
  // decision 4a), and the generated one's device is `generated`: the site plan
  // answers it and the server refuses nothing for it. Counting it here calls it
  // carried in Devices' Source line and in the console's root-password sentence,
  // and freezes a site the server does not freeze. At Carillon on 2026-10-12
  // access points re-claimed as generated read carried in both places (#440);
  // that their reason was this one is read from the code, because it is the only
  // reason readiness gives a `generated` row that this function counted, and not
  // from that site's response.
  // `held_record` is the contract's word for which writer it was.
  const serials = new Set(
    r.devices.filter((d) => isCarried(d)).map((d) => d.serial),
  );
  const unreadable = new Set(r.devices.filter((d) => d.reason === "unrecognised_source").map((d) => d.serial));
  const exits = new Map<string, ExitReading>();
  for (const d of r.devices) if (d.exit && serials.has(d.serial)) exits.set(d.serial, d.exit);
  // Either kind freezes the site, because the server refuses on both.
  return { serials, unreadable, frozen: serials.size > 0 || unreadable.size > 0, exits };
}

/** Whether readiness's row is a carried access point. It is not one where the
 * reason is `not_carried` or `unrecognised_source`, nor where it is `held_on_cell`
 * and the record held is an operator's generated apply's. */
function isCarried(d: DeviceReadiness): boolean {
  if (d.reason === "not_carried" || d.reason === "unrecognised_source") return false;
  return !heldGenerated(d);
}

/** The marker on a device Zenith will not generate for.
 *
 * An unrecognised source does not say "carried": Zenith holds no document for
 * such a device and claiming it carries one would be the page asserting
 * something nobody knows (#156). It gets its own word. */
export function CarriedMark({ reason }: { reason?: string }) {
  const r = !reason ? undefined : listed(DEVICE_REASON, reason) ? DEVICE_REASON[reason] : unrecognisedReason(reason);
  if (reason === "unrecognised_source") {
    return <Pill tone="critical" title={DEVICE_REASON.unrecognised_source.next}>source not understood</Pill>;
  }
  // Restates ADR-0019 decision 6, #1186, ADR-0213 decision 4a — the word `applied` is backed by
  // the controller's own acknowledgement of the apply, not by Zenith's record of having sent one.
  // Where that acknowledgement carried no generation the device reads `sent, not confirmed`
  // instead, so this sentence stays true of every device it is shown for.
  // `not_carried` is the one reason a device that is **not** carried is given
  // (`internal/configure/readiness.go`: no source row, or `generated`), so its
  // pill does not say *carried*: Operate › Devices reads the same device
  // *generated* from the same response (`ConfigurationCell`), and the two
  // disagreed (UX-019, the 2026-11-01 review). It still says *not imported*,
  // because readiness counts it against the site (ADR-0019 decision 6).
  const whose = reason === "not_carried" ? "generated" : "carried";
  return (
    <Pill tone={r?.tone ?? "good"} title={r ? r.next : "Its carried configuration was applied and the controller acknowledged taking it, and it is current."}>
      {whose}{r ? ` · ${r.label}` : " · applied"}
    </Pill>
  );
}

/** The honest sentence a frozen site gets, wherever a configuration change
 * would otherwise be attempted (§11.2). It says what is refused, what is not,
 * and what lifts it.
 *
 * Restates ADR-0226 decision 1b and ADR-0021 / ADR-0057 — reconciliation takes a
 * named set of a site's carried access points, down to one, and a switch is not
 * part of it. A switch's own way out of `carried` is its release (ADR-0427), one
 * switch at a time from Design › Wired, and reconciliation never moves it.
 *
 * Restates ADR-0019 decision 4 — a generated configuration is refused for a carried
 * device, with no override (the guard, `carriedIn`, refuses the plan as well as the
 * apply); intent may still be authored; and reconciliation is the only exit.
 *
 * **This sentence has been wrong three times now, each time in a different
 * direction, so it is worth knowing what it claims.** It said "decided and not
 * built" for nine days after #270 built the act (#919); it then said the act
 * exists over `/v1` with no screen for it, which #920 made false by building one;
 * and it said reconciliation happens "for the whole site's access points at once
 * rather than device by device", which ADR-0226 decision 1b made false by ruling
 * the unit a named subset (#1279). What it claims now is that the act takes a set
 * the operator chooses and that the screen exists and is reached from here — and
 * the link is the claim, so a reader who follows it is testing the sentence.
 *
 * **What is in the open and what is behind the disclosure** (#1678). In the open:
 * what is blocked, that nothing overrides it, what can still be done, the link with
 * what it opens (a set the operator chooses, and a plan read before it is applied),
 * and the warning about a source nobody understands. Behind *Learn more*, moved whole
 * and reworded to the glossary, with every clause it had: why a generated
 * configuration is refused, what a reconciliation covers and leaves, and what its
 * plan shows. The disclosure is last because the pages that draw this pass their
 * own sentence as `children`, which continues the paragraph.
 *
 * **The disclosure holds a warning's *why*, and that is a ruled exception** to
 * `web/COPY.md`'s rule that a warning's *why* goes in the comment and the spec and
 * not behind *Learn more*. Ruled on #1678 on 2026-10-12 (note `4001321718`): *"As
 * built, as a named exception"*, on the ground that what an operator must know
 * before acting stays in the open: blocked, no override, edits are kept, reconcile
 * and read its plan. It is this banner's exception and no other notice's. The same
 * note rules "the exit" as the word for reconciliation, here and on the Reconcile
 * tab. */
export function FrozenNotice({ carried, unreadable, exits, children }: { carried: number; unreadable: number; exits?: Map<string, ExitReading>; children?: ReactNode }) {
  const total = carried + unreadable;
  const covered = exits?.size ?? 0;
  return (
    <div className="notice" role="status">
      <strong>
        {total === 1 ? "One access point here does" : `${total} access points here do`} not run a configuration
        generated here.
      </strong>{" "}
      {covered > 0
        ? <>
            A reconciliation here covered {covered === total ? (total === 1 ? "it" : "all of them") : `${covered} of them`}, and
            each stays carried until the controller and the access point confirm it runs what was sent. Until every one
            has, generating a configuration for this site and sending one are blocked, and there is no override.
          </>
        : <>Until this site is reconciled, generating a configuration for it and sending one are blocked, and there is no
          override.</>}{" "}You can still edit networks and radios here, and your changes are kept.{" "}
      <ScopeLink to="/design/changes/reconcile" title="Open Reconcile, where this site's reconciliation is planned and approved">
        Reconcile this site
      </ScopeLink>: you choose which access points it covers, and you read its plan before you apply it.
      {unreadable > 0 && (
        <>
          {" "}
          <strong>
            {unreadable === 1 ? "One of them has" : `${unreadable} of them have`} a configuration source this
            version does not understand
          </strong>
          , which reconciliation will not fix. Nothing an operator can do here will move
          {unreadable === 1 ? " it" : " them"}: someone has to find out what wrote the record. The
          {unreadable === 1 ? " device is" : " devices are"} still running whatever
          {unreadable === 1 ? " it was" : " they were"}, because every write refuses rather than guessing.
        </>
      )}
      {exits && covered > 0 && <ExitLines exits={exits} />}
      {children}
      <LearnMore summary="Why this is blocked, and what reconciling does">
        <p>
          A generated configuration is the <em>complete</em> desired state. Applying one would replace everything on
          those devices and drop every section this version does not model, which is most of a vendor&rsquo;s
          configuration.
        </p>
        <p>
          Reconciliation is the exit. It covers the access points you choose: all of them, or a named few, down
          to one. The ones you leave out keep the configuration they already carry, and a generated configuration is
          still refused for them.
        </p>
        <p>
          Reconciling is a plan you read and then apply. The plan shows what the venue configuration would be, what
          each access point loses by it, and what <em>every</em> access point here would be served afterward.
          Applying it carries that plan&rsquo;s digest back.
        </p>
      </LearnMore>
    </div>
  );
}

/** What each exit word means (#2249). Whether another reconciliation can change
 * it is the server's `reconcile_again`, never worked out here.
 *
 * Restates ADR-0507 decisions 1c and 2a and the ruling of 2026-10-29 ("Build now,
 * 3.x refuses") — the device-side read is asked at most once per device per apply,
 * and on a build it is not measured on nothing is sent and the exit refuses, so
 * `already_asked`, `disagrees` and `unread` wait for the site's next apply and
 * `unmeasured_build` cannot be moved by one. Restates ADR-0094, ADR-0271, ADR-0319
 * and ADR-0488 — each earlier term is named by what it found. If the read's licence
 * or its measured builds change, the sentences of those four are what go false. */
export const EXIT_OUTCOME: Record<ExitOutcome, { label: string; next: string }> = {
  not_read_yet: { label: "not checked yet",
    next: "The check that moves it out of carried has not reached it since the reconciliation. It runs on its own; there is nothing to do yet." },
  cell_not_read: { label: "controller not answering about it",
    next: "The controller could not be asked about this access point the last time the check ran. It is asked again on its own." },
  sent_its_own_since: { label: "sent its own configuration since",
    next: "It was sent its own carried configuration after the reconciliation, so it runs that one. Reconciling it again sends it the site's." },
  not_running_held: { label: "not running it yet",
    next: "It is offline, has not reported, or does not yet run what its controller holds. This can clear on its own, with nothing sent, once it does." },
  cell_did_not_take: { label: "controller did not take it",
    next: "Its controller was not read holding a configuration stored since the reconciliation, so nothing confirms the controller took it. Read the reconciliation's outcome in History, then reconcile again." },
  holds_other_document: { label: "controller holds another configuration",
    next: "What its controller holds is not the configuration the reconciliation sent it. Reconciling again sends it once more." },
  record_disagrees: { label: "controller record disagrees",
    next: "Its controller's record names another configuration than the one it holds, the mark a known controller race leaves. The site's next reconciliation is its route." },
  unmeasured_build: { label: "firmware not measured",
    next: "The final check reads the access point itself, and that read is not measured on its firmware build, so nothing is sent and it stays carried. Reconciling again cannot change that; upgrading it to a measured build, or measuring this build, can." },
  already_asked: { label: "already checked under this reconciliation",
    next: "The access point was read once under this reconciliation and did not confirm it, and it is read only once per reconciliation. Reconciling again lets it be read again." },
  disagrees: { label: "runs another configuration",
    next: "The access point itself reports running a configuration other than the one the reconciliation sent. Reconciling again sends it once more." },
  unread: { label: "its answer did not come back",
    next: "The access point was read and its answer did not come back whole. It is read once per reconciliation, so reconciling again is how it is read again." },
};

/** The exit's word for one access point, or the unrecognised fallback, with
 * what another reconciliation would do for it as the server states it. */
export function exitOf(e: ExitReading) {
  const words = listed(EXIT_OUTCOME, e.outcome)
    ? EXIT_OUTCOME[e.outcome]
    : { label: e.outcome, next: "This page does not recognize the reason the server gave. Reload the page: a newer version may know it." };
  return { ...words, again: e.reconcile_again };
}

/** The pill's tone for what another reconciliation would do: serious where it
 * cannot move the device, warned where it is the route, and informational while
 * the exit may still pass on its own. */
export function exitTone(again: string): "serious" | "warn" | "info" {
  return again === "cannot" ? "serious" : again === "wait" ? "info" : "warn";
}

/** Per access point a reconciliation covered and that is still carried, why, and
 * whether reconciling again can help (#2249). The server decides every word. */
export function ExitLines({ exits }: { exits: Map<string, ExitReading> }) {
  const rows = [...exits.entries()].sort(([a], [b]) => a.localeCompare(b));
  const cannot = rows.filter(([, e]) => exitOf(e).again === "cannot").length;
  const waiting = rows.filter(([, e]) => exitOf(e).again === "wait").length;
  return (
    <div data-exit-lines>
      <p>
        Why {rows.length === 1 ? "it is" : "each is"} still carried
        {cannot > 0 && <>; reconciling again cannot move {cannot === 1 ? "one of them" : `${cannot} of them`}</>}
        {waiting > 0 && <>; {waiting === 1 ? "one is" : `${waiting} are`} still being checked, and needs nothing yet</>}:
      </p>
      <ul className="problem-fields">
        {rows.map(([serial, e]) => {
          const o = exitOf(e);
          return (
            <li key={serial} data-exit-serial={serial} data-exit-again={o.again}>
              <code>{serial}</code> <Pill tone={exitTone(o.again)}>{o.label}</Pill>{" "}
              {o.next}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
